/**
 * Quran Reader Preview Step
 * Shows real QCF-rendered Quran page (page 1 = Al-Fatihah)
 * Uses the same rendering pipeline as the ReadPage
 */

import { useState, useEffect, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Settings, Bookmark, X } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { OnboardingStep } from '../OnboardingStep';
import { getAyahsByPage, getSurahs } from '@/services/quran.service';
import { useQcfFont } from '@/hooks/useQcfFont';
import QuranWord from '@/components/read/QuranWord';
import type { Ayah, SurahListItem, QuranWord as QuranWordType } from '@/types/quran';

/** Group words by lineNumber for Mushaf layout */
function groupWordsByLine(ayahs: Ayah[]) {
  const lineMap = new Map<number, (QuranWordType & { surahNumber?: number; ayahNumberInSurah?: number })[]>();

  for (const ayah of ayahs) {
    if (!ayah.words?.length) continue;
    for (const word of ayah.words) {
      const lineNum = word.lineNumber ?? 0;
      if (!lineMap.has(lineNum)) lineMap.set(lineNum, []);
      lineMap.get(lineNum)!.push({
        ...word,
        surahNumber: ayah.surahNumber,
        ayahNumberInSurah: ayah.numberInSurah,
      });
    }
  }

  return Array.from(lineMap.entries())
    .sort(([a], [b]) => a - b)
    .map(([lineNumber, words]) => ({ lineNumber, words }));
}

function QuranReaderMockup() {
  const [ayahs, setAyahs] = useState<Ayah[]>([]);
  const [surahs, setSurahs] = useState<SurahListItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [pageData, surahList] = await Promise.all([
          getAyahsByPage(1),
          getSurahs(),
        ]);
        if (!cancelled) {
          setAyahs(pageData);
          setSurahs(surahList);
          setLoading(false);
        }
      } catch (err) {
        console.error('Failed to load onboarding quran preview:', err);
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const allWords = useMemo(() => ayahs.flatMap(a => a.words ?? []), [ayahs]);
  const loadedPages = useQcfFont(allWords, { version: 'v2' });
  const lines = useMemo(() => groupWordsByLine(ayahs), [ayahs]);

  // Find surah info for page 1
  const surahInfo = surahs.find(s => s.number === 1);

  return (
    <div className="w-full max-w-md mx-auto bg-background rounded-2xl overflow-hidden shadow-2xl border border-border pointer-events-none select-none">
      {/* Top Controls Bar */}
      <div className="bg-card/50 backdrop-blur-xl border-b border-border/50 p-2.5">
        <div className="flex items-center justify-between mb-1.5">
          <button className="p-1.5 rounded-lg hover:bg-muted/50">
            <X className="w-4 h-4" />
          </button>
          <div className="text-center flex-1">
            <p className="text-xs font-semibold">หน้า 1</p>
            <p className="text-[10px] text-muted-foreground">ญุซอ์ 1</p>
          </div>
          <div className="flex gap-1">
            <button className="p-1.5 rounded-lg hover:bg-muted/50">
              <Bookmark className="w-4 h-4" />
            </button>
            <button className="p-1.5 rounded-lg hover:bg-muted/50">
              <Settings className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Mode Tabs */}
        <div className="flex gap-1 mb-1.5">
          <div className="flex-1 py-1 rounded-md bg-primary/10 text-center">
            <span className="text-[10px] font-medium text-primary">อ่าน</span>
          </div>
          <div className="flex-1 py-1 rounded-md text-center">
            <span className="text-[10px] font-medium text-muted-foreground">ตะดับบุร</span>
          </div>
        </div>

        {/* Surah Selector */}
        <div className="px-2 py-1 rounded-md bg-muted/30 text-center">
          <p className="text-[10px] font-medium">
            {surahInfo ? `${surahInfo.name} · ${surahInfo.englishName}` : 'อัล-ฟาตีหะห์'}
          </p>
        </div>
      </div>

      {/* Quran Content — Real QCF Rendering */}
      <div className="quran-page-bg px-3 py-4">
        {loading ? (
          <div className="space-y-3 py-4">
            <Skeleton className="h-10 w-3/4 mx-auto rounded-xl" />
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-6 w-full" />
            ))}
          </div>
        ) : (
          <div className={cn('mushaf-page qcf-scale-small')}>
            {/* Surah Header */}
            {surahInfo && (
              <div className="surah-header-ornament mb-2">
                <span className="surah-arabic-name">{surahInfo.name}</span>
                <span className="surah-english-name">
                  {surahInfo.number}. {surahInfo.englishName} · {surahInfo.englishNameTranslation}
                </span>
              </div>
            )}

            {/* QCF Mushaf Lines */}
            {lines.map((line, lineIdx) => (
              <div key={`line-1-${line.lineNumber}-${lineIdx}`}>
                <div className="mushaf-line">
                  {line.words.map((word, wordIdx) => (
                    <QuranWord
                      key={`w-${word.id ?? wordIdx}-${word.position}`}
                      word={word}
                      isFontLoaded={loadedPages.has(word.pageNumber ?? 0)}
                      fontVersion="v2"
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Page Navigation */}
      <div className="bg-background/30 backdrop-blur-xl border-t border-border/50 p-2 flex items-center justify-between">
        <button className="flex items-center gap-0.5 px-2 py-1 rounded-lg text-xs font-medium hover:bg-muted">
          <ChevronLeft className="w-3.5 h-3.5" />
          ก่อนหน้า
        </button>
        <span className="text-[10px] text-muted-foreground">1 / 604</span>
        <button className="flex items-center gap-0.5 px-2 py-1 rounded-lg text-xs font-medium hover:bg-muted">
          ถัดไป
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

export function QuranReaderPreviewStep() {
  return (
    <OnboardingStep
      title="อ่านอัลกุรอานแบบมุศฮัฟ"
      description="อ่าน 604 หน้า พร้อมโหมดตะดับบุร สีตัจวีด ตัฟซีรภาษาไทย และฟังเสียงเชคผู้อ่านได้"
      illustration={<QuranReaderMockup />}
    />
  );
}

export default QuranReaderPreviewStep;
